"use client";

import { useState } from "react";

import Form from "next/form";

import cn from "classnames";

import { ExternalLinkIcon } from "@/components/icons/externalLink.icon";
import { MonitoringPlanCard } from "@/components/monitoringPlanDrawer/monitoringPlanCard/monitoringPlanCard.component";
import { PromotionLegalModal } from "@/components/promotionLegalModal/promotionLegalModal.component";
import { useRemoveQueryParam } from "@/components/queryLink/queryLink.component";
import { Text } from "@/components/text/text.component";

import { MONITORING_PLAN_SEARCH_PARAM } from "@/app/blu/shop/constants";
import { MONITORING_PAGE } from "@/constants/page.constants";
import type { RecommendedMonitoringPlansQueryResult } from "@/sanity.types";

import { MonitoringPlanDrawerAction } from "./monitoringPlanDrawerAction.component";

export const MonitoringPlanDrawerContent = ({
  action,
  plans,
  selectedMonitoringId,
}: MonitoringPlanDrawerContentProps) => {
  const removeQueryParam = useRemoveQueryParam();

  // Default selected plan is the plan that is currently selected in the basket or the first plan in the list if no plan is selected.
  const [draftSelectedPlan, setDraftSelectedPlan] = useState<Plan | null>(
    () => {
      const defaultPlan = plans.find(
        (plan) => plan?.id === selectedMonitoringId,
      );
      const planId = defaultPlan?.id || plans[0]?.id;
      const found = plans.find((plan) => plan?.id === planId);
      return found ?? null;
    },
  );

  const closeDrawer = () => removeQueryParam(MONITORING_PLAN_SEARCH_PARAM);

  const renderPlanCards = () => {
    if (!plans?.length) {
      return (
        <Text variant="body3" component="p" className="text-greyscale-body">
          No monitoring plans are available right now. Please try again later.
        </Text>
      );
    }
    return plans.map((plan) => {
      if (!plan?.id) return null;
      return (
        <MonitoringPlanCard
          key={plan.id}
          badges={plan.chips || []}
          promotions={plan.promotions}
          monitoringLegal={plan.monitoring?.legal}
          title={plan.name}
          description={plan.longDescription}
          price={plan.salesPrice ?? plan.listPrice}
          imageSrc={plan.featuredImage?.url?.trim() || undefined}
          imageAlt={plan.featuredImage?.alt?.trim() || plan.name}
          isSelected={draftSelectedPlan?.id === plan.id}
          onClick={() => setDraftSelectedPlan(plan)}
        />
      );
    });
  };

  const handleSave = async () => {
    if (!draftSelectedPlan?.id) return;
    const kitId = document
      .querySelector('input[name="kitId"]')
      ?.getAttribute("value");
    try {
      await action(kitId, draftSelectedPlan);
      closeDrawer();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Form
      action={handleSave}
      className="flex h-full max-h-full min-h-0 flex-col overflow-hidden">
      <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain p-6">
        <Text
          variant="subtitle1"
          component="h3"
          className="text-greyscale-body">
          {plans.length > 1 ? "Choose your monitoring plan" : "You're getting"}
        </Text>
        {renderPlanCards()}
        <PromotionLegalModal
          legalCallout={draftSelectedPlan?.promotions?.legalCallout}
        />
        <a
          href={MONITORING_PAGE}
          data-testid="learn-more-link"
          className="text-greyscale-body flex w-fit items-center gap-1.5 underline">
          <Text variant="body3" component="span">
            Learn more about your monitoring options
          </Text>
          <ExternalLinkIcon className="hidden md:block" />
        </a>
      </section>
      <footer
        className={cn(
          "border-greyscale-strength/20 flex shrink-0 justify-end px-6 py-4 shadow-[0_-12px_24px_0_rgba(0,0,0,0.14)] md:px-10 md:py-6",
          { hidden: plans.length === 1 },
        )}>
        <MonitoringPlanDrawerAction
          plans={plans}
          selectedPlan={draftSelectedPlan}
        />
      </footer>
    </Form>
  );
};

export type Plan = RecommendedMonitoringPlansQueryResult[number];
export type MonitoringPlanDrawerContentProps = {
  plans: RecommendedMonitoringPlansQueryResult;
  selectedMonitoringId?: string | null;
  action: (productId: string | null | undefined, planId: Plan) => Promise<void>;
};
